import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
  applyColorModeToDocument,
  createAppTheme,
  persistColorMode,
  readStoredColorMode,
} from '../theme';

const ColorModeContext = createContext({
  mode: 'dark',
  setMode: () => {},
  toggleColorMode: () => {},
});

export function ColorModeProvider({ children }) {
  const [mode, setModeState] = useState(() => readStoredColorMode());

  useEffect(() => {
    applyColorModeToDocument(mode);
  }, [mode]);

  const setMode = useCallback((next) => {
    const resolved = next === 'light' ? 'light' : 'dark';
    setModeState(resolved);
    persistColorMode(resolved);
    applyColorModeToDocument(resolved);
  }, []);

  const toggleColorMode = useCallback(() => {
    setMode(mode === 'light' ? 'dark' : 'light');
  }, [mode, setMode]);

  const theme = useMemo(() => createAppTheme(mode), [mode]);
  const value = useMemo(
    () => ({ mode, setMode, toggleColorMode }),
    [mode, setMode, toggleColorMode],
  );

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export function useColorMode() {
  return useContext(ColorModeContext);
}
